import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { HeaderNav } from "@/components/HeaderNav";
import api, { formatApiError } from "@/lib/api";

const EXPIRED = "This password reset link has expired or has already been used. Please request a new one.";
const inputCls = "w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 pr-11 text-[#F3F4F6] focus:border-brass focus:outline-none transition-colors";

function PasswordField({ label, value, onChange, show, testid }) {
  return (
    <div>
      <label className="text-sm text-[#9CA3AF]">{label}</label>
      <input data-testid={testid} type={show ? "text" : "password"} required value={value}
        onChange={(e) => onChange(e.target.value)} className={`mt-1.5 ${inputCls}`} autoComplete="new-password" />
    </div>
  );
}

function Expired() {
  return (
    <div className="cg-card p-7 space-y-4" data-testid="reset-link-expired">
      <p className="text-[#E8EAEE] leading-relaxed">{EXPIRED}</p>
      <Link to="/forgot-password" data-testid="request-new-link" className="cg-link text-sm">Request a new reset link</Link>
    </div>
  );
}

function Done() {
  return (
    <div className="cg-card p-7 space-y-5" data-testid="reset-success">
      <p className="text-[#E8EAEE] leading-relaxed">Your password has been updated. You can now sign in with your new password.</p>
      <Link to="/login" data-testid="reset-back-to-signin" className="inline-block bg-brass text-obsidian font-medium px-6 py-3 rounded-full cg-scale-btn hover:bg-amber2">
        Back to sign in
      </Link>
    </div>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/reset-password/validate", { params: { token } })
      .then(({ data }) => setState(data.valid ? "form" : "expired"))
      .catch(() => setState("expired"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Your password needs at least 8 characters."); return; }
    if (pw !== pw2) { setError("The two passwords don't match. Please type the same password in both boxes."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setState("done");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      if (msg === EXPIRED) setState("expired"); else setError(msg);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <HeaderNav />
      <div className="max-w-md mx-auto px-5 pt-24 pb-16 cg-fade-up">
        <h1 className="font-serif text-4xl text-[#F3F4F6] mb-8">Create a new password</h1>
        {state === "checking" && <p className="text-[#9CA3AF]" data-testid="reset-checking">Checking your link…</p>}
        {state === "expired" && <Expired />}
        {state === "done" && <Done />}
        {state === "form" && (
          <form onSubmit={submit} className="cg-card p-7 space-y-5" data-testid="reset-form">
            <p className="text-sm text-[#9CA3AF]" data-testid="password-requirements">Your password must be at least 8 characters long.</p>
            <PasswordField label="New password" value={pw} onChange={setPw} show={show} testid="reset-password-input" />
            <PasswordField label="Confirm new password" value={pw2} onChange={setPw2} show={show} testid="reset-confirm-input" />
            <button type="button" data-testid="toggle-password-visibility" onClick={() => setShow(!show)}
              className="flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}{show ? "Hide passwords" : "Show passwords"}
            </button>
            {error && <p data-testid="reset-error" className="text-sm text-terracotta">{error}</p>}
            <button data-testid="reset-submit-button" disabled={busy}
              className="w-full bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
              {busy ? "Saving…" : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
